import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.array.map.js';
import { jsx } from 'react/jsx-runtime';
import React, { useState, useEffect } from 'react';
import { Tabs } from '@mui/base/Tabs';
import { TabsList } from '@mui/base/TabsList';
import classNames from 'classnames';
import { getThemeClassNames } from '@cognite/cogs-utils';
import { Skeleton } from '../skeleton/skeleton.js';
import { SegmentedControlButton } from './segmented-control.button.js';

/**
 * SegmentedControl
 * =============================================================================
 * With the SegmentedControl, you can filter, sort or make a display choice,
 * like toggle between different views of the same dataset.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-segmentedcontrol--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 *  <SegmentedControl>
 *    <SegmentedControl.Button key="1">Histogram</SegmentedControl.Button>
 *    <SegmentedControl.Button key="2">Graph view</SegmentedControl.Button>
 *    <SegmentedControl.Button key="3">List view</SegmentedControl.Button>
 *  </SegmentedControl>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 *
 * @param currentKey       - The key of the active button.
 * @param controlled       - When set to true, the SegmentedControl will not modify the selected tab when clicked.
 * @param fullWidth        - If true, the component will stretch over the entire available width.
 * @param onButtonClicked  - This function runs when a button is clicked. It passes the key of the clicked button.
 * @param size             - The size of the segmentedcontrol. (`small` or '`default`)
 * @param skeleton         - The skeleton version of the segmentedcontrol.
 * @param disabled         - Disable all tabs in the component
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-segmentedcontrol--base)
 */
var SegmentedControl = function SegmentedControl(_ref) {
  var children = _ref.children,
    className = _ref.className,
    propsCurrentKey = _ref.currentKey,
    _ref$onButtonClicked = _ref.onButtonClicked,
    onButtonClicked = _ref$onButtonClicked === undefined ? function () {} : _ref$onButtonClicked,
    disabled = _ref.disabled,
    _ref$size = _ref.size,
    size = _ref$size === undefined ? 'medium' : _ref$size,
    fullWidth = _ref.fullWidth,
    controlled = _ref.controlled,
    skeleton = _ref.skeleton,
    _ref$type = _ref.type,
    type = _ref$type === undefined ? 'default' : _ref$type,
    inverted = _ref.inverted,
    themeProps = _ref.themeProps;
  var tabs = children.map(function (tab) {
    return tab.key;
  });
  var _useState = useState("" + (propsCurrentKey || children[0].key)),
    currentKey = _useState[0],
    setKey = _useState[1];
  useEffect(function () {
    if (propsCurrentKey) {
      setKey("" + propsCurrentKey);
    }
  }, [propsCurrentKey]);
  if (skeleton) return jsx(Skeleton.Rectangle, {
    height: "36px",
    width: "240px"
  });
  return jsx(Tabs, {
    value: currentKey,
    onChange: function onChange(_, value) {
      if (disabled || !value) return;
      if (!controlled) {
        setKey("" + value);
      }
      onButtonClicked(value);
    },
    className: classNames('cogs-v10', 'cogs-segmented-control'),
    children: jsx(TabsList, {
      className: classNames.apply(undefined, ['cogs-v10', 'cogs-segmented-control__list', disabled && 'cogs-segmented-control__list--disabled', "cogs-segmented-control__list--size-" + size, fullWidth && 'cogs-segmented-control__list--full-width', "cogs-segmented-control__list--type-" + type].concat(getThemeClassNames(inverted, themeProps), [className])),
      children: tabs.map(function (tab, i) {
        var key = tab;
        var isCurrentKey = key === currentKey;
        var childProps = children[i].props;
        var isButtonDisabled = childProps.disabled || disabled;
        return /*#__PURE__*/React.cloneElement(children[i], {
          className: classNames(childProps.className),
          onClick: function onClick(e) {
            if (isButtonDisabled) return;
            if (!controlled) {
              setKey("" + key);
            }
            onButtonClicked(key);
            if (childProps.onClick) childProps.onClick(e);
          },
          key: key,
          active: isCurrentKey,
          _tabKey: key,
          disabled: isButtonDisabled,
          children: childProps.children
        });
      })
    })
  });
};
SegmentedControl.Button = SegmentedControlButton;

export { SegmentedControl };
